export interface PlatformDocSection {
  id: string;
  title: string;
  group: string;
  content: string;
}

export const platformSections: PlatformDocSection[] = [
  // ═══════════════════════════════════════════
  //  GETTING STARTED
  // ═══════════════════════════════════════════
  {
    id: "platform-overview",
    title: "Platform Overview",
    group: "Getting Started",
    content: `
      <p><strong>DocIQ</strong> is an intelligent document management and automation platform. It combines document ingestion, AI-powered extraction, workflow automation, and enterprise integrations into a single unified system.</p>

      <div class="wf-callout wf-callout-info">
        DocIQ handles the entire document lifecycle — from upload and processing to search, comparison, and export — with built-in AI capabilities and a visual workflow builder.
      </div>

      <h3>Core capabilities</h3>
      <ul class="wf-list">
        <li><strong>Document Management</strong> — Upload, organize, search, and retrieve documents with full-text indexing and metadata tagging.</li>
        <li><strong>AI Processing</strong> — OCR extraction, intelligent parsing, LLM-based summarization, and structured data extraction powered by Google Gemini.</li>
        <li><strong>Workflow Automation</strong> — Visual node-based workflow builder for creating end-to-end document processing pipelines without code.</li>
        <li><strong>Enterprise Integration</strong> — Connect with OneDrive, Gmail, Outlook, MongoDB, REST APIs, webhooks, and MCP servers.</li>
        <li><strong>User Management</strong> — Role-based access control with users, groups, roles, and SSO support.</li>
        <li><strong>Reporting</strong> — Audit trails, processing reports, and analytics dashboards.</li>
      </ul>

      <h3>Dashboard</h3>
      <p>The Dashboard is your central hub when you log into DocIQ. It provides a real-time overview of your platform activity.</p>

      <div class="wf-screenshot">
        <img src="https://nightly-dms.sgp1.digitaloceanspaces.com/2c4d273f-235d-401f-bfc2-8c0df07136ab.png" alt="DocIQ Dashboard" />
      </div>

      <table>
        <thead><tr><th>Widget</th><th>Description</th></tr></thead>
        <tbody>
          <tr><td><strong>Total Documents</strong></td><td>Count of all documents uploaded and processed across all stores</td></tr>
          <tr><td><strong>Processing Queue</strong></td><td>Documents currently being processed or waiting in the queue</td></tr>
          <tr><td><strong>Active Workflows</strong></td><td>Number of workflows currently enabled and running</td></tr>
          <tr><td><strong>Recent Activity</strong></td><td>Timeline of recent document uploads, processing completions, and workflow executions</td></tr>
          <tr><td><strong>Storage Usage</strong></td><td>Current storage consumption across all document stores</td></tr>
          <tr><td><strong>Quick Actions</strong></td><td>Shortcuts to upload documents, create workflows, and manage templates</td></tr>
        </tbody>
      </table>
    `,
  },

  // ═══════════════════════════════════════════
  //  DOCUMENT MANAGEMENT
  // ═══════════════════════════════════════════
  {
    id: "document-upload",
    title: "Document Upload",
    group: "Document Management",
    content: `
      <p>DocIQ supports uploading documents individually or in bulk. Uploaded files are stored securely and can be automatically routed to processing workflows.</p>

      <div class="wf-screenshot">
        <img src="https://nightly-dms.sgp1.digitaloceanspaces.com/4808c17f-44ac-45bd-94f1-449d87a281f7.png" alt="Document Store" />
      </div>

      <h3>Supported file formats</h3>
      <div class="wf-method-list" style="margin-bottom: 0.75rem;">
        <code>PDF</code> <code>PNG</code> <code>JPG</code> <code>JPEG</code> <code>TIFF</code> <code>DOCX</code> <code>XLSX</code> <code>CSV</code> <code>TXT</code> <code>EML</code> <code>MSG</code>
      </div>

      <h3>Upload methods</h3>
      <div class="wf-steps">
        <div class="wf-step">
          <div class="wf-step-num">1</div>
          <div>
            <p class="wf-step-title">Manual Upload</p>
            <p class="wf-step-desc">Drag and drop files or click the upload area in the Document Upload page. You can upload multiple files at once. Select the target document store before uploading.</p>
          </div>
        </div>
        <div class="wf-step">
          <div class="wf-step-num">2</div>
          <div>
            <p class="wf-step-title">API Upload</p>
            <p class="wf-step-desc">Use the DocIQ REST API to upload documents programmatically. Send files via multipart form data to the upload endpoint with the document store ID.</p>
          </div>
        </div>
        <div class="wf-step">
          <div class="wf-step-num">3</div>
          <div>
            <p class="wf-step-title">Workflow Triggers</p>
            <p class="wf-step-desc">Documents can be ingested automatically through workflow triggers — Gmail, Outlook, OneDrive, or HTTP endpoints.</p>
          </div>
        </div>
      </div>

      <h3>Upload configuration</h3>
      <table>
        <thead><tr><th>Setting</th><th>Description</th></tr></thead>
        <tbody>
          <tr><td><strong>Document Store</strong></td><td>Select which store the uploaded document belongs to. Each store can have its own processing pipeline.</td></tr>
          <tr><td><strong>Auto-Process</strong></td><td>When enabled, uploaded documents are automatically sent to the configured workflow for processing.</td></tr>
          <tr><td><strong>Tags</strong></td><td>Add metadata tags at upload time for easier search and categorization later.</td></tr>
          <tr><td><strong>Max File Size</strong></td><td>Configurable per business settings. Default is 25 MB per file.</td></tr>
        </tbody>
      </table>

      <div class="wf-callout wf-callout-tip">
        <p class="wf-callout-label">Tip</p>
        <ul>
          <li>For bulk uploads, compress files into a ZIP and use the bulk upload feature — DocIQ will extract and process each file individually.</li>
          <li>Set up auto-processing to eliminate manual steps after upload.</li>
        </ul>
      </div>
    `,
  },
  {
    id: "document-processing",
    title: "Document Processing",
    group: "Document Management",
    content: `
      <p>Document processing is where DocIQ applies AI and automation to extract meaningful data from your uploaded files. Processing can be triggered manually or automatically through workflows.</p>

      <div class="wf-screenshot">
        <img src="https://nightly-dms.sgp1.digitaloceanspaces.com/bebe102b-73c3-44e0-b353-77b8bd45a8ec.png" alt="Document Processing" />
      </div>

      <h3>Processing pipeline</h3>
      <div class="wf-steps">
        <div class="wf-step">
          <div class="wf-step-num">1</div>
          <div>
            <p class="wf-step-title">Document Ingestion</p>
            <p class="wf-step-desc">The uploaded file is received and stored. Metadata (filename, type, size, upload time) is recorded automatically.</p>
          </div>
        </div>
        <div class="wf-step">
          <div class="wf-step-num">2</div>
          <div>
            <p class="wf-step-title">OCR & Text Extraction</p>
            <p class="wf-step-desc">For scanned documents and images, OCR extracts machine-readable text. Native PDFs have text extracted directly. Multiple OCR engines are available (built-in, Cloud Vision, Gemini).</p>
          </div>
        </div>
        <div class="wf-step">
          <div class="wf-step-num">3</div>
          <div>
            <p class="wf-step-title">Structured Data Extraction</p>
            <p class="wf-step-desc">AI models parse the extracted text into structured fields based on your configured templates — invoice numbers, dates, amounts, vendor names, line items, etc.</p>
          </div>
        </div>
        <div class="wf-step">
          <div class="wf-step-num">4</div>
          <div>
            <p class="wf-step-title">Validation & Enrichment</p>
            <p class="wf-step-desc">Extracted data is validated against business rules. Confidence scores indicate extraction quality. Low-confidence fields can be flagged for manual review.</p>
          </div>
        </div>
        <div class="wf-step">
          <div class="wf-step-num wf-step-done">&#10003;</div>
          <div>
            <p class="wf-step-title">Storage & Indexing</p>
            <p class="wf-step-desc">Processed data is stored in the database and indexed for full-text search. The original document is preserved alongside the extracted data.</p>
          </div>
        </div>
      </div>

      <h3>Processing status</h3>
      <table>
        <thead><tr><th>Status</th><th>Meaning</th></tr></thead>
        <tbody>
          <tr><td><strong>Queued</strong></td><td>Document is waiting to be processed</td></tr>
          <tr><td><strong>Processing</strong></td><td>Currently being processed by the pipeline</td></tr>
          <tr><td><strong>Completed</strong></td><td>Processing finished successfully with extracted data available</td></tr>
          <tr><td><strong>Failed</strong></td><td>Processing encountered an error — check logs for details</td></tr>
          <tr><td><strong>Review Required</strong></td><td>Extraction confidence is below threshold — manual review needed</td></tr>
        </tbody>
      </table>

      <div class="wf-callout wf-callout-info">
        You can reprocess any document at any time by clicking the "Reprocess" button. This is useful after updating templates or extraction rules.
      </div>
    `,
  },
  {
    id: "document-search",
    title: "Document Search",
    group: "Document Management",
    content: `
      <p>DocIQ provides powerful full-text search across all your uploaded and processed documents. Search through document content, metadata, extracted fields, and tags.</p>

      <div class="wf-screenshot">
        <img src="https://nightly-dms.sgp1.digitaloceanspaces.com/a1dd7836-d099-4ab4-8a50-bf7eeff3a739.png" alt="Document Search" />
      </div>

      <h3>Search capabilities</h3>
      <ul class="wf-list">
        <li><strong>Full-Text Search</strong> — Search across the entire text content of all processed documents. Matches are highlighted in context.</li>
        <li><strong>Metadata Filters</strong> — Filter by document type, upload date, processing status, document store, and custom tags.</li>
        <li><strong>Field-Level Search</strong> — Search within specific extracted fields (e.g., find all invoices where vendor = "Acme Corp").</li>
        <li><strong>Boolean Operators</strong> — Use AND, OR, NOT operators for complex queries.</li>
        <li><strong>Fuzzy Matching</strong> — Handles typos and partial matches for more forgiving searches.</li>
      </ul>

      <h3>Search filters</h3>
      <table>
        <thead><tr><th>Filter</th><th>Description</th></tr></thead>
        <tbody>
          <tr><td><strong>Document Store</strong></td><td>Limit search to a specific document store</td></tr>
          <tr><td><strong>Date Range</strong></td><td>Filter by upload date or processing date</td></tr>
          <tr><td><strong>File Type</strong></td><td>Filter by file format (PDF, image, email, etc.)</td></tr>
          <tr><td><strong>Status</strong></td><td>Filter by processing status</td></tr>
          <tr><td><strong>Tags</strong></td><td>Filter by assigned tags</td></tr>
          <tr><td><strong>Confidence Score</strong></td><td>Filter by extraction confidence threshold</td></tr>
        </tbody>
      </table>

      <div class="wf-callout wf-callout-tip">
        <p class="wf-callout-label">Search tips</p>
        <ul>
          <li>Use quotes for exact phrase matching: <code>"invoice number 12345"</code></li>
          <li>Combine filters with text search for precise results</li>
          <li>Save frequently used searches as presets for quick access</li>
        </ul>
      </div>
    `,
  },
  {
    id: "compare-documents",
    title: "Compare Documents",
    group: "Document Management",
    content: `
      <p>The Compare Documents feature lets you diff two documents or two versions of the same document. It compares extracted data field-by-field and produces a detailed comparison report.</p>

      <div class="wf-screenshot">
        <img src="https://nightly-dms.sgp1.digitaloceanspaces.com/e2b14160-5624-44cd-8e95-a344e42527f1.png" alt="Compare Documents" />
      </div>

      <h3>How comparison works</h3>
      <div class="wf-steps">
        <div class="wf-step">
          <div class="wf-step-num">1</div>
          <div>
            <p class="wf-step-title">Select documents</p>
            <p class="wf-step-desc">Choose two documents to compare. They can be from the same or different document stores. Both must have completed processing with extracted data.</p>
          </div>
        </div>
        <div class="wf-step">
          <div class="wf-step-num">2</div>
          <div>
            <p class="wf-step-title">Field mapping</p>
            <p class="wf-step-desc">DocIQ automatically maps corresponding fields between the two documents. You can adjust the mapping if needed for documents with different structures.</p>
          </div>
        </div>
        <div class="wf-step">
          <div class="wf-step-num">3</div>
          <div>
            <p class="wf-step-title">Comparison results</p>
            <p class="wf-step-desc">View a side-by-side comparison with differences highlighted. Each field shows match status, old value, new value, and a similarity score.</p>
          </div>
        </div>
      </div>

      <h3>Comparison output</h3>
      <table>
        <thead><tr><th>Field</th><th>Description</th></tr></thead>
        <tbody>
          <tr><td><strong>Match Score</strong></td><td>Overall similarity percentage between the two documents (0-100%)</td></tr>
          <tr><td><strong>Matched Fields</strong></td><td>Fields that are identical in both documents</td></tr>
          <tr><td><strong>Mismatched Fields</strong></td><td>Fields with different values — shown with old vs. new highlighting</td></tr>
          <tr><td><strong>Missing Fields</strong></td><td>Fields present in one document but absent in the other</td></tr>
        </tbody>
      </table>

      <div class="wf-callout wf-callout-info">
        Document comparison is also available as a workflow node (<strong>Compare Data</strong>) for automated comparison pipelines.
      </div>
    `,
  },
  {
    id: "field-mapping",
    title: "Field Mapping & Parsing",
    group: "Document Management",
    content: `
      <p>Field mapping defines how raw extracted text is transformed into structured data fields. Field parsing rules determine how each field value is cleaned, formatted, and validated.</p>

      <div class="wf-screenshot">
        <img src="https://nightly-dms.sgp1.digitaloceanspaces.com/31d11738-e4f6-4482-b05a-90fa59fbfc05.png" alt="Template Field Mapping" />
      </div>

      <h3>Field mapping</h3>
      <p>When you configure a document store or template, you define which fields to extract and how they map to your data model.</p>

      <table>
        <thead><tr><th>Concept</th><th>Description</th></tr></thead>
        <tbody>
          <tr><td><strong>Source Field</strong></td><td>The raw field name as it appears in the extracted document data</td></tr>
          <tr><td><strong>Target Field</strong></td><td>The standardized field name in your data model</td></tr>
          <tr><td><strong>Data Type</strong></td><td>The expected type: string, number, date, boolean, array, object</td></tr>
          <tr><td><strong>Required</strong></td><td>Whether this field must be present for processing to succeed</td></tr>
          <tr><td><strong>Default Value</strong></td><td>Fallback value when the field is not found in the document</td></tr>
        </tbody>
      </table>

      <h3>Parsing rules</h3>
      <p>Parsing rules transform raw extracted values into clean, usable data.</p>

      <ul class="wf-list">
        <li><strong>Date Parsing</strong> — Automatically detects and normalizes various date formats (MM/DD/YYYY, DD-Mon-YY, ISO 8601, etc.) into a consistent format.</li>
        <li><strong>Number Parsing</strong> — Strips currency symbols, thousand separators, and whitespace to produce clean numeric values.</li>
        <li><strong>Text Normalization</strong> — Trims whitespace, handles case conversion, and removes unwanted characters.</li>
        <li><strong>Regex Extraction</strong> — Apply custom regular expressions to extract specific patterns from text (e.g., invoice numbers, phone numbers).</li>
        <li><strong>Lookup Tables</strong> — Map extracted codes to human-readable values using predefined lookup tables.</li>
      </ul>

      <div class="wf-callout wf-callout-tip">
        <p class="wf-callout-label">Best practices</p>
        <ul>
          <li>Define field mappings before uploading documents to ensure consistent extraction from the start.</li>
          <li>Use the template preview feature to test your mappings against sample documents.</li>
          <li>Set up validation rules to flag extraction errors early in the pipeline.</li>
        </ul>
      </div>
    `,
  },
  {
    id: "template-management",
    title: "Template Management",
    group: "Document Management",
    content: `
      <p>Templates define the extraction schema for different document types. Each template specifies which fields to extract, their data types, and validation rules. A document store is linked to one or more templates.</p>

      <h3>Creating a template</h3>
      <div class="wf-steps">
        <div class="wf-step">
          <div class="wf-step-num">1</div>
          <div>
            <p class="wf-step-title">Define template name and type</p>
            <p class="wf-step-desc">Give your template a descriptive name (e.g., "Invoice Template", "Purchase Order Template") and select the document category.</p>
          </div>
        </div>
        <div class="wf-step">
          <div class="wf-step-num">2</div>
          <div>
            <p class="wf-step-title">Add extraction fields</p>
            <p class="wf-step-desc">Define the fields you want to extract. For each field, specify the name, data type, whether it's required, and any parsing rules.</p>
          </div>
        </div>
        <div class="wf-step">
          <div class="wf-step-num">3</div>
          <div>
            <p class="wf-step-title">Configure AI extraction prompts</p>
            <p class="wf-step-desc">For AI-powered extraction (Gemini), define the JSON schema and any additional instructions for the model to follow.</p>
          </div>
        </div>
        <div class="wf-step">
          <div class="wf-step-num">4</div>
          <div>
            <p class="wf-step-title">Test with sample documents</p>
            <p class="wf-step-desc">Upload a sample document and preview the extraction results. Adjust field definitions and prompts until the output matches expectations.</p>
          </div>
        </div>
        <div class="wf-step">
          <div class="wf-step-num wf-step-done">&#10003;</div>
          <div>
            <p class="wf-step-title">Link to document store</p>
            <p class="wf-step-desc">Assign the template to one or more document stores. All documents uploaded to those stores will use this template for extraction.</p>
          </div>
        </div>
      </div>

      <h3>Template features</h3>
      <table>
        <thead><tr><th>Feature</th><th>Description</th></tr></thead>
        <tbody>
          <tr><td><strong>Field Schema</strong></td><td>JSON-based schema defining all extractable fields with types and constraints</td></tr>
          <tr><td><strong>Validation Rules</strong></td><td>Built-in and custom rules to validate extracted values (range checks, format validation, etc.)</td></tr>
          <tr><td><strong>Version History</strong></td><td>Track changes to templates over time. Roll back to previous versions if needed.</td></tr>
          <tr><td><strong>Template Cloning</strong></td><td>Duplicate an existing template as a starting point for a new document type</td></tr>
          <tr><td><strong>Shared Templates</strong></td><td>Templates can be shared across multiple document stores within the organization</td></tr>
        </tbody>
      </table>
    `,
  },

  // ═══════════════════════════════════════════
  //  INTEGRATIONS & API
  // ═══════════════════════════════════════════
  {
    id: "webhook-config",
    title: "Webhook Configuration",
    group: "Integrations & API",
    content: `
      <p>Webhooks allow DocIQ to send real-time notifications to external systems when events occur. Configure outgoing webhooks to push data to any HTTP endpoint.</p>

      <h3>Setting up a webhook</h3>
      <div class="wf-steps">
        <div class="wf-step">
          <div class="wf-step-num">1</div>
          <div>
            <p class="wf-step-title">Navigate to Integration Settings</p>
            <p class="wf-step-desc">Go to <strong>Settings &rarr; Integrations &rarr; Webhooks</strong> in the DocIQ dashboard.</p>
          </div>
        </div>
        <div class="wf-step">
          <div class="wf-step-num">2</div>
          <div>
            <p class="wf-step-title">Add a new webhook</p>
            <p class="wf-step-desc">Click "Add Webhook" and provide the target URL, HTTP method (POST/PUT), and any custom headers (e.g., authorization tokens).</p>
          </div>
        </div>
        <div class="wf-step">
          <div class="wf-step-num">3</div>
          <div>
            <p class="wf-step-title">Select trigger events</p>
            <p class="wf-step-desc">Choose which events fire the webhook: document uploaded, processing completed, workflow finished, extraction failed, etc.</p>
          </div>
        </div>
        <div class="wf-step">
          <div class="wf-step-num wf-step-done">&#10003;</div>
          <div>
            <p class="wf-step-title">Test and activate</p>
            <p class="wf-step-desc">Send a test payload to verify your endpoint receives data correctly, then activate the webhook.</p>
          </div>
        </div>
      </div>

      <h3>Webhook payload</h3>
      <p>Each webhook sends a JSON payload containing the event type, timestamp, and the relevant data object.</p>

      <div class="wf-code-block">
        <div class="wf-code-header">Example payload</div>
        <pre><code>{
  "event": "document.processed",
  "timestamp": "2025-01-15T10:30:00Z",
  "data": {
    "document_id": "doc_abc123",
    "store_id": "store_xyz",
    "status": "completed",
    "extracted_fields": { ... },
    "confidence_score": 0.95
  }
}</code></pre>
      </div>

      <h3>Webhook events</h3>
      <table>
        <thead><tr><th>Event</th><th>Fires when</th></tr></thead>
        <tbody>
          <tr><td><code>document.uploaded</code></td><td>A new document is uploaded to any store</td></tr>
          <tr><td><code>document.processed</code></td><td>Document processing completes successfully</td></tr>
          <tr><td><code>document.failed</code></td><td>Document processing fails with an error</td></tr>
          <tr><td><code>workflow.completed</code></td><td>A workflow execution finishes</td></tr>
          <tr><td><code>workflow.error</code></td><td>A workflow execution encounters an error</td></tr>
        </tbody>
      </table>

      <div class="wf-callout wf-callout-info">
        Webhooks retry up to 3 times with exponential backoff if the endpoint returns a non-2xx status code. Failed deliveries are logged in the webhook history.
      </div>
    `,
  },
  {
    id: "db-config",
    title: "Database Configuration",
    group: "Integrations & API",
    content: `
      <p>DocIQ uses MongoDB as its primary database. The database configuration page allows administrators to manage connection settings, collections, and data retention policies.</p>

      <div class="wf-screenshot">
        <img src="https://nightly-dms.sgp1.digitaloceanspaces.com/6b003cc0-abc2-453b-b63f-a990f6cf0f02.png" alt="Database Configuration" />
      </div>

      <h3>Connection settings</h3>
      <table>
        <thead><tr><th>Setting</th><th>Description</th></tr></thead>
        <tbody>
          <tr><td><strong>Connection String</strong></td><td>MongoDB connection URI (e.g., <code>mongodb://host:port/dbname</code>). Supports replica sets and Atlas clusters.</td></tr>
          <tr><td><strong>Database Name</strong></td><td>The target database for DocIQ document storage</td></tr>
          <tr><td><strong>Authentication</strong></td><td>Username/password or certificate-based authentication for the MongoDB connection</td></tr>
          <tr><td><strong>Connection Pool</strong></td><td>Configure min/max connections for optimal performance</td></tr>
        </tbody>
      </table>

      <h3>Collections</h3>
      <p>DocIQ creates and manages several collections automatically:</p>
      <ul class="wf-list">
        <li><strong>documents</strong> — Stores document metadata, extracted data, and processing status</li>
        <li><strong>workflows</strong> — Workflow definitions, configurations, and execution history</li>
        <li><strong>users</strong> — User accounts, roles, and permissions</li>
        <li><strong>audit_logs</strong> — Complete audit trail of all system actions</li>
        <li><strong>templates</strong> — Extraction templates and field schemas</li>
      </ul>

      <h3>Data retention</h3>
      <p>Configure automatic data cleanup policies to manage storage growth.</p>
      <table>
        <thead><tr><th>Policy</th><th>Description</th></tr></thead>
        <tbody>
          <tr><td><strong>Document Retention</strong></td><td>Auto-delete documents older than N days (configurable per store)</td></tr>
          <tr><td><strong>Audit Log Retention</strong></td><td>Keep audit logs for a specified period (default: 90 days)</td></tr>
          <tr><td><strong>Workflow History</strong></td><td>Retain workflow execution history for N days</td></tr>
        </tbody>
      </table>
    `,
  },
  {
    id: "api-endpoints",
    title: "API Endpoints",
    group: "Integrations & API",
    content: `
      <p>DocIQ exposes a comprehensive REST API for programmatic access to all platform features. All API requests require authentication via API keys or JWT tokens.</p>

      <div class="wf-screenshot">
        <img src="https://nightly-dms.sgp1.digitaloceanspaces.com/4fec8b8b-287c-4fac-bc8f-665bac84f25c.png" alt="API Endpoints" />
      </div>

      <h3>Authentication</h3>
      <p>Include your API key in the request header:</p>
      <div class="wf-code-block">
        <div class="wf-code-header">Request header</div>
        <pre><code>Authorization: Bearer YOUR_API_KEY
Content-Type: application/json</code></pre>
      </div>

      <h3>Core endpoints</h3>
      <table>
        <thead><tr><th>Method</th><th>Endpoint</th><th>Description</th></tr></thead>
        <tbody>
          <tr><td><code>POST</code></td><td><code>/api/documents/upload</code></td><td>Upload a new document</td></tr>
          <tr><td><code>GET</code></td><td><code>/api/documents/:id</code></td><td>Retrieve document details and extracted data</td></tr>
          <tr><td><code>POST</code></td><td><code>/api/documents/search</code></td><td>Search documents with filters</td></tr>
          <tr><td><code>DELETE</code></td><td><code>/api/documents/:id</code></td><td>Delete a document</td></tr>
          <tr><td><code>POST</code></td><td><code>/api/documents/:id/reprocess</code></td><td>Reprocess a document</td></tr>
        </tbody>
      </table>

      <h3>Workflow endpoints</h3>
      <table>
        <thead><tr><th>Method</th><th>Endpoint</th><th>Description</th></tr></thead>
        <tbody>
          <tr><td><code>GET</code></td><td><code>/api/workflows</code></td><td>List all workflows</td></tr>
          <tr><td><code>POST</code></td><td><code>/api/workflows</code></td><td>Create a new workflow</td></tr>
          <tr><td><code>GET</code></td><td><code>/api/workflows/:id</code></td><td>Get workflow details</td></tr>
          <tr><td><code>PUT</code></td><td><code>/api/workflows/:id</code></td><td>Update a workflow</td></tr>
          <tr><td><code>DELETE</code></td><td><code>/api/workflows/:id</code></td><td>Delete a workflow</td></tr>
          <tr><td><code>POST</code></td><td><code>/api/workflows/:id/execute</code></td><td>Manually trigger a workflow</td></tr>
          <tr><td><code>GET</code></td><td><code>/api/workflows/:id/executions</code></td><td>Get execution history</td></tr>
        </tbody>
      </table>

      <h3>Template endpoints</h3>
      <table>
        <thead><tr><th>Method</th><th>Endpoint</th><th>Description</th></tr></thead>
        <tbody>
          <tr><td><code>GET</code></td><td><code>/api/templates</code></td><td>List all templates</td></tr>
          <tr><td><code>POST</code></td><td><code>/api/templates</code></td><td>Create a new template</td></tr>
          <tr><td><code>PUT</code></td><td><code>/api/templates/:id</code></td><td>Update a template</td></tr>
          <tr><td><code>DELETE</code></td><td><code>/api/templates/:id</code></td><td>Delete a template</td></tr>
        </tbody>
      </table>

      <div class="wf-callout wf-callout-info">
        All API responses follow a consistent format: <code>{ "success": true/false, "data": {...}, "message": "..." }</code>. Rate limiting applies at 100 requests per minute per API key.
      </div>
    `,
  },

  // ═══════════════════════════════════════════
  //  USER MANAGEMENT
  // ═══════════════════════════════════════════
  {
    id: "users-management",
    title: "Users",
    group: "User Management",
    content: `
      <p>Manage user accounts, profiles, and access within your DocIQ organization. Administrators can create users, assign roles, and control what each user can access.</p>

      <div class="wf-screenshot">
        <img src="https://nightly-dms.sgp1.digitaloceanspaces.com/e09309ff-dbab-4e3f-a2a1-cdb170291982.png" alt="User List" />
      </div>

      <h3>User list</h3>
      <p>The Users page shows all accounts in your organization with their status, role, and last activity. Use the search bar and filters to find specific users.</p>

      <table>
        <thead><tr><th>Column</th><th>Description</th></tr></thead>
        <tbody>
          <tr><td><strong>Name</strong></td><td>User's full name and email address</td></tr>
          <tr><td><strong>Role</strong></td><td>Assigned role (Admin, Editor, Viewer, Custom)</td></tr>
          <tr><td><strong>Status</strong></td><td>Active, Inactive, or Pending invitation</td></tr>
          <tr><td><strong>Groups</strong></td><td>Groups the user belongs to</td></tr>
          <tr><td><strong>Last Active</strong></td><td>Timestamp of the user's last login or action</td></tr>
        </tbody>
      </table>

      <h3>Managing users</h3>
      <ul class="wf-list">
        <li><strong>Create User</strong> — Add a new user with name, email, role, and group assignments. An invitation email is sent automatically.</li>
        <li><strong>Edit User</strong> — Update name, role, group membership, and notification preferences.</li>
        <li><strong>Deactivate User</strong> — Disable a user's access without deleting their account. Their data and history are preserved.</li>
        <li><strong>Delete User</strong> — Permanently remove a user account. This action cannot be undone.</li>
        <li><strong>Reset Password</strong> — Send a password reset link to the user's email address.</li>
      </ul>
    `,
  },
  {
    id: "groups-management",
    title: "Groups",
    group: "User Management",
    content: `
      <p>Groups let you organize users into logical teams and apply permissions at the group level. Instead of managing access per user, assign document store access and workflow permissions to groups.</p>

      <div class="wf-screenshot">
        <img src="https://nightly-dms.sgp1.digitaloceanspaces.com/d10250b2-e707-4fa3-980c-f6c8cfab980c.png" alt="Groups" />
      </div>

      <h3>Group features</h3>
      <table>
        <thead><tr><th>Feature</th><th>Description</th></tr></thead>
        <tbody>
          <tr><td><strong>Group Members</strong></td><td>Add or remove users from a group. A user can belong to multiple groups.</td></tr>
          <tr><td><strong>Document Store Access</strong></td><td>Grant the group read, write, or admin access to specific document stores.</td></tr>
          <tr><td><strong>Workflow Permissions</strong></td><td>Allow the group to view, edit, or execute specific workflows.</td></tr>
          <tr><td><strong>Template Access</strong></td><td>Control which extraction templates the group can view or modify.</td></tr>
        </tbody>
      </table>

      <h3>Creating a group</h3>
      <div class="wf-steps">
        <div class="wf-step">
          <div class="wf-step-num">1</div>
          <div>
            <p class="wf-step-title">Define group details</p>
            <p class="wf-step-desc">Navigate to <strong>User Management &rarr; Groups</strong>. Click "Create Group" and enter the group name and description.</p>
          </div>
        </div>
        <div class="wf-step">
          <div class="wf-step-num">2</div>
          <div>
            <p class="wf-step-title">Add members</p>
            <p class="wf-step-desc">Search and select users to add to the group. You can add members during creation or anytime later.</p>
          </div>
        </div>
        <div class="wf-step">
          <div class="wf-step-num wf-step-done">&#10003;</div>
          <div>
            <p class="wf-step-title">Set permissions</p>
            <p class="wf-step-desc">Configure which document stores, workflows, and templates this group can access and what level of access they have.</p>
          </div>
        </div>
      </div>
    `,
  },
  {
    id: "roles-permissions",
    title: "Roles & Permissions",
    group: "User Management",
    content: `
      <p>DocIQ uses role-based access control (RBAC) to manage what users can see and do. Each user is assigned a role that determines their permissions across the platform.</p>

      <div class="wf-screenshot">
        <img src="https://nightly-dms.sgp1.digitaloceanspaces.com/6ff33411-32ec-4848-bf00-4e388b042e1e.png" alt="Roles & Permissions" />
      </div>

      <h3>Built-in roles</h3>
      <table>
        <thead><tr><th>Role</th><th>Permissions</th></tr></thead>
        <tbody>
          <tr><td><strong>Super Admin</strong></td><td>Full access to everything — user management, system settings, all document stores, all workflows. Can manage SSO and API keys.</td></tr>
          <tr><td><strong>Admin</strong></td><td>Manage users within their groups, configure document stores and workflows, view audit logs. Cannot change system-level settings.</td></tr>
          <tr><td><strong>Editor</strong></td><td>Upload and process documents, create and edit workflows, manage templates. Cannot manage users or system settings.</td></tr>
          <tr><td><strong>Viewer</strong></td><td>Read-only access to documents, search results, and workflow execution history. Cannot modify any data.</td></tr>
        </tbody>
      </table>

      <h3>Custom roles</h3>
      <p>Create custom roles with specific permission combinations for your organization's needs.</p>

      <ul class="wf-list">
        <li><strong>Document Permissions</strong> — Upload, read, edit, delete, reprocess, export</li>
        <li><strong>Workflow Permissions</strong> — View, create, edit, delete, execute, manage triggers</li>
        <li><strong>Template Permissions</strong> — View, create, edit, delete, assign to stores</li>
        <li><strong>User Permissions</strong> — View users, create users, edit users, manage roles, manage groups</li>
        <li><strong>System Permissions</strong> — Access settings, manage integrations, view audit logs, manage API keys</li>
      </ul>

      <div class="wf-callout wf-callout-info">
        When a user belongs to multiple groups with different permission levels, the <strong>highest permission</strong> applies (most permissive wins).
      </div>
    `,
  },
  {
    id: "invitations",
    title: "User Invitations",
    group: "User Management",
    content: `
      <p>Invite new users to join your DocIQ organization via email. Invitations include role assignments and group memberships so new users have the right access from day one.</p>

      <div class="wf-screenshot">
        <img src="https://nightly-dms.sgp1.digitaloceanspaces.com/5adef0bc-6c7c-4970-8ec6-cea3ecbdc296.png" alt="User Invitations" />
      </div>

      <h3>Sending invitations</h3>
      <div class="wf-steps">
        <div class="wf-step">
          <div class="wf-step-num">1</div>
          <div>
            <p class="wf-step-title">Enter email addresses</p>
            <p class="wf-step-desc">Go to <strong>User Management &rarr; Invitations</strong>. Enter one or more email addresses. You can invite multiple users at once.</p>
          </div>
        </div>
        <div class="wf-step">
          <div class="wf-step-num">2</div>
          <div>
            <p class="wf-step-title">Assign role and groups</p>
            <p class="wf-step-desc">Select the role and groups for the invited users. All users in a batch invitation receive the same role and group assignments.</p>
          </div>
        </div>
        <div class="wf-step">
          <div class="wf-step-num wf-step-done">&#10003;</div>
          <div>
            <p class="wf-step-title">Send invitation</p>
            <p class="wf-step-desc">Click "Send". Each user receives an email with a link to set their password and activate their account.</p>
          </div>
        </div>
      </div>

      <h3>Invitation status</h3>
      <table>
        <thead><tr><th>Status</th><th>Description</th></tr></thead>
        <tbody>
          <tr><td><strong>Pending</strong></td><td>Invitation sent but user hasn't accepted yet</td></tr>
          <tr><td><strong>Accepted</strong></td><td>User has clicked the link and set up their account</td></tr>
          <tr><td><strong>Expired</strong></td><td>Invitation link expired (default: 7 days). Resend to generate a new link.</td></tr>
          <tr><td><strong>Revoked</strong></td><td>Invitation was manually cancelled by an administrator</td></tr>
        </tbody>
      </table>

      <div class="wf-callout wf-callout-tip">
        <p class="wf-callout-label">Tip</p>
        <ul>
          <li>Expired invitations can be resent with one click from the Invitations page.</li>
          <li>When SSO is enabled, invited users can sign in with their organization's identity provider instead of setting a password.</li>
        </ul>
      </div>
    `,
  },

  // ═══════════════════════════════════════════
  //  REPORTS & ANALYTICS
  // ═══════════════════════════════════════════
  {
    id: "audit-trail",
    title: "Audit Trail",
    group: "Reports & Analytics",
    content: `
      <p>The Audit Trail provides a complete, tamper-proof record of all actions performed in your DocIQ instance. Every user action, system event, and API call is logged with full context.</p>

      <div class="wf-screenshot">
        <img src="https://nightly-dms.sgp1.digitaloceanspaces.com/e9cd60fc-2294-45cc-8d17-111aebd7e956.png" alt="Audit Trail" />
      </div>

      <h3>What is tracked</h3>
      <table>
        <thead><tr><th>Category</th><th>Events tracked</th></tr></thead>
        <tbody>
          <tr><td><strong>Authentication</strong></td><td>Login, logout, password changes, SSO sign-ins, failed login attempts</td></tr>
          <tr><td><strong>Documents</strong></td><td>Upload, view, download, delete, reprocess, field edits</td></tr>
          <tr><td><strong>Workflows</strong></td><td>Create, edit, delete, activate, deactivate, execution start/complete/fail</td></tr>
          <tr><td><strong>Users</strong></td><td>Create, edit, deactivate, delete, role changes, group changes</td></tr>
          <tr><td><strong>Settings</strong></td><td>Configuration changes, integration updates, API key generation</td></tr>
          <tr><td><strong>API</strong></td><td>All API calls with request details, response status, and caller identity</td></tr>
        </tbody>
      </table>

      <h3>Audit log fields</h3>
      <ul class="wf-list">
        <li><strong>Timestamp</strong> — When the event occurred (UTC)</li>
        <li><strong>User</strong> — Who performed the action (user name, email, or "System" for automated events)</li>
        <li><strong>Action</strong> — What was done (e.g., "document.uploaded", "workflow.activated")</li>
        <li><strong>Resource</strong> — The affected entity (document ID, workflow name, user email)</li>
        <li><strong>Details</strong> — Additional context (changed fields, old/new values, IP address)</li>
        <li><strong>Status</strong> — Whether the action succeeded or failed</li>
      </ul>

      <h3>Filtering and export</h3>
      <p>Use the filter panel to narrow down audit records by date range, user, action type, or resource. Export filtered results as CSV for external analysis or compliance reporting.</p>

      <div class="wf-callout wf-callout-info">
        Audit logs are retained based on your configured retention policy (default: 90 days). For compliance needs, configure longer retention or set up automatic export to external storage.
      </div>
    `,
  },
  {
    id: "user-activity-report",
    title: "User Activity Report",
    group: "Reports & Analytics",
    content: `
      <p>The User Activity Report shows what each user has been doing in DocxIQ — sign-ins, documents accessed, and actions taken over a selected period. Use it for adoption tracking, security oversight, and compliance.</p>

      <h3>What it shows</h3>
      <table>
        <thead><tr><th>Metric</th><th>Description</th></tr></thead>
        <tbody>
          <tr><td><strong>Sign-ins</strong></td><td>Login count and last-active time per user, including failed attempts.</td></tr>
          <tr><td><strong>Documents accessed</strong></td><td>Files viewed, uploaded, downloaded, or edited by each user.</td></tr>
          <tr><td><strong>Actions performed</strong></td><td>Workflow runs, searches, exports, and configuration changes.</td></tr>
          <tr><td><strong>Active vs. inactive</strong></td><td>Identify power users and accounts with little or no activity.</td></tr>
        </tbody>
      </table>

      <h3>Filtering and export</h3>
      <p>Filter by user, role, or date range to focus the report, then export the results as CSV for audits or management reviews.</p>

      <h3>Common use cases</h3>
      <ul class="wf-list">
        <li><strong>Adoption</strong> — see who is actively using DocxIQ and who may need onboarding.</li>
        <li><strong>Security</strong> — spot unusual access patterns or dormant accounts to deactivate.</li>
        <li><strong>Compliance</strong> — produce a per-user activity record on demand.</li>
      </ul>

      <div class="wf-callout wf-callout-tip">
        <p class="wf-callout-label">Tip</p>
        <p>Pair this with the Audit Trail for full context — the User Activity Report summarizes per user, while the Audit Trail records every individual event.</p>
      </div>
    `,
  },
  {
    id: "processing-reports",
    title: "Document Processing Reports",
    group: "Reports & Analytics",
    content: `
      <p>Processing reports give you visibility into document processing performance, extraction accuracy, and throughput metrics across your DocIQ instance.</p>

      <div class="wf-screenshot">
        <img src="https://nightly-dms.sgp1.digitaloceanspaces.com/9d2b61c8-fc47-4f51-8a2e-acb0032873ad.png" alt="Document Processing Analytics" />
      </div>

      <h3>Report types</h3>
      <table>
        <thead><tr><th>Report</th><th>Description</th></tr></thead>
        <tbody>
          <tr><td><strong>Processing Summary</strong></td><td>Overview of documents processed, success/failure rates, and average processing time over a selected period</td></tr>
          <tr><td><strong>Extraction Accuracy</strong></td><td>Confidence score distribution across documents, fields with lowest accuracy, and manual review rates</td></tr>
          <tr><td><strong>Throughput</strong></td><td>Documents processed per hour/day/week, queue depth over time, and peak processing periods</td></tr>
          <tr><td><strong>Error Analysis</strong></td><td>Most common processing errors, failure patterns by document type, and suggested fixes</td></tr>
          <tr><td><strong>Store Breakdown</strong></td><td>Per-store processing metrics — volume, accuracy, and average processing time for each document store</td></tr>
        </tbody>
      </table>

      <h3>Key metrics</h3>
      <ul class="wf-list">
        <li><strong>Success Rate</strong> — Percentage of documents that complete processing without errors</li>
        <li><strong>Average Confidence</strong> — Mean extraction confidence score across all processed documents</li>
        <li><strong>Average Processing Time</strong> — Mean time from upload to processing completion</li>
        <li><strong>Manual Review Rate</strong> — Percentage of documents flagged for manual review due to low confidence</li>
        <li><strong>Queue Wait Time</strong> — Average time documents spend waiting in the processing queue</li>
      </ul>

      <div class="wf-callout wf-callout-tip">
        <p class="wf-callout-label">Optimization tips</p>
        <ul>
          <li>High manual review rates often indicate the extraction template needs refinement. Check which fields are consistently low-confidence.</li>
          <li>If processing times are increasing, check for large file sizes or complex document layouts that might need different OCR settings.</li>
          <li>Use the store breakdown report to identify which document types need the most attention.</li>
        </ul>
      </div>
    `,
  },

  // ═══════════════════════════════════════════
  //  SETTINGS
  // ═══════════════════════════════════════════
  {
    id: "business-settings",
    title: "Business Settings",
    group: "Settings",
    content: `
      <p>Business Settings control organization-wide configuration for your DocIQ instance. These settings affect all users, document stores, and workflows.</p>

      <div class="wf-screenshot">
        <img src="https://nightly-dms.sgp1.digitaloceanspaces.com/a4999280-5552-48d2-a2a7-1a305e8073a8.png" alt="Business Settings" />
      </div>

      <h3>General settings</h3>
      <table>
        <thead><tr><th>Setting</th><th>Description</th></tr></thead>
        <tbody>
          <tr><td><strong>Organization Name</strong></td><td>Your organization's display name shown across the platform</td></tr>
          <tr><td><strong>Time Zone</strong></td><td>Default time zone for scheduling, reports, and audit log timestamps</td></tr>
          <tr><td><strong>Date Format</strong></td><td>Preferred date display format (MM/DD/YYYY, DD/MM/YYYY, YYYY-MM-DD)</td></tr>
          <tr><td><strong>Language</strong></td><td>Primary language for OCR processing and AI extraction</td></tr>
          <tr><td><strong>Currency</strong></td><td>Default currency for financial field extraction and formatting</td></tr>
        </tbody>
      </table>

      <h3>Document settings</h3>
      <table>
        <thead><tr><th>Setting</th><th>Description</th></tr></thead>
        <tbody>
          <tr><td><strong>Max Upload Size</strong></td><td>Maximum file size for document uploads (default: 25 MB)</td></tr>
          <tr><td><strong>Allowed File Types</strong></td><td>Whitelist of accepted file extensions for upload</td></tr>
          <tr><td><strong>Default OCR Engine</strong></td><td>Which OCR engine to use when not specified: Built-in, Cloud Vision, or Gemini</td></tr>
          <tr><td><strong>Confidence Threshold</strong></td><td>Minimum confidence score for auto-acceptance (below this triggers manual review)</td></tr>
          <tr><td><strong>Auto-Process on Upload</strong></td><td>Global toggle for automatic processing when documents are uploaded</td></tr>
        </tbody>
      </table>

      <h3>Notification settings</h3>
      <table>
        <thead><tr><th>Setting</th><th>Description</th></tr></thead>
        <tbody>
          <tr><td><strong>Email Notifications</strong></td><td>Enable/disable email notifications for processing completion, errors, and invitations</td></tr>
          <tr><td><strong>SMTP Configuration</strong></td><td>Custom SMTP server settings for outgoing emails (host, port, credentials, TLS)</td></tr>
          <tr><td><strong>Notification Recipients</strong></td><td>Default recipients for system alerts and error notifications</td></tr>
        </tbody>
      </table>

      <div class="wf-callout wf-callout-info">
        Changes to business settings take effect immediately. Some settings (like OCR engine changes) only apply to newly processed documents — existing documents retain their original processing results.
      </div>
    `,
  },
  {
    id: "sso-config",
    title: "SSO Configuration",
    group: "Settings",
    content: `
      <p>Single Sign-On (SSO) allows users to authenticate with your organization's identity provider (IdP) instead of using DocIQ-specific credentials. DocIQ supports SAML 2.0 and OAuth 2.0 / OpenID Connect protocols.</p>

      <div class="wf-screenshot">
        <img src="https://nightly-dms.sgp1.digitaloceanspaces.com/0fc72238-5d18-4624-8033-ed4566c21fba.png" alt="SSO Configuration" />
      </div>

      <h3>Supported providers</h3>
      <div class="wf-method-list" style="margin-bottom: 0.75rem;">
        <code>Azure AD</code> <code>Google Workspace</code> <code>Okta</code> <code>OneLogin</code> <code>Auth0</code> <code>Custom SAML</code> <code>Custom OIDC</code>
      </div>

      <h3>SAML 2.0 setup</h3>
      <div class="wf-steps">
        <div class="wf-step">
          <div class="wf-step-num">1</div>
          <div>
            <p class="wf-step-title">Get DocIQ metadata</p>
            <p class="wf-step-desc">Go to <strong>Settings &rarr; SSO</strong>. Copy the Service Provider (SP) metadata URL, Entity ID, and ACS URL to configure in your IdP.</p>
          </div>
        </div>
        <div class="wf-step">
          <div class="wf-step-num">2</div>
          <div>
            <p class="wf-step-title">Configure your IdP</p>
            <p class="wf-step-desc">In your identity provider (Azure AD, Okta, etc.), create a new SAML application using the DocIQ SP metadata. Configure attribute mapping for email, name, and groups.</p>
          </div>
        </div>
        <div class="wf-step">
          <div class="wf-step-num">3</div>
          <div>
            <p class="wf-step-title">Enter IdP metadata in DocIQ</p>
            <p class="wf-step-desc">Paste your IdP's metadata URL or upload the metadata XML file. DocIQ will automatically extract the SSO URL, certificate, and entity ID.</p>
          </div>
        </div>
        <div class="wf-step">
          <div class="wf-step-num wf-step-done">&#10003;</div>
          <div>
            <p class="wf-step-title">Test and enable</p>
            <p class="wf-step-desc">Click "Test Connection" to verify the SSO flow works correctly. Once verified, enable SSO for your organization.</p>
          </div>
        </div>
      </div>

      <h3>SSO settings</h3>
      <table>
        <thead><tr><th>Setting</th><th>Description</th></tr></thead>
        <tbody>
          <tr><td><strong>Enforce SSO</strong></td><td>When enabled, all users must sign in via SSO. Direct username/password login is disabled.</td></tr>
          <tr><td><strong>Auto-Provision</strong></td><td>Automatically create DocIQ accounts for new SSO users. Assign a default role and group.</td></tr>
          <tr><td><strong>Group Mapping</strong></td><td>Map IdP groups to DocIQ groups for automatic group assignment during SSO login.</td></tr>
          <tr><td><strong>Session Duration</strong></td><td>How long SSO sessions remain valid before requiring re-authentication (default: 8 hours).</td></tr>
        </tbody>
      </table>

      <div class="wf-callout wf-callout-tip">
        <p class="wf-callout-label">Best practices</p>
        <ul>
          <li>Always test SSO with a non-admin account before enabling the "Enforce SSO" setting.</li>
          <li>Keep at least one local admin account as a backup in case SSO encounters issues.</li>
          <li>Use group mapping to automatically assign the correct roles and document store access based on your IdP groups.</li>
        </ul>
      </div>
    `,
  },

  // ═══════════════════════════════════════════
  //  RELEASE NOTES
  // ═══════════════════════════════════════════
  {
    id: "release-notes",
    title: "Release Notes",
    group: "Release Notes",
    content: `
      <p>Stay up to date with the latest features, improvements, and fixes in DocIQ.</p>

      <div class="wf-use-cases">
        <div class="wf-use-case">
          <p class="wf-use-case-title">v2.5.0 — Workflow Automation & MCP Integration</p>
          <p class="wf-use-case-flow">February 2026</p>
          <ul class="wf-list">
            <li><strong>Visual Workflow Builder</strong> — Build document processing pipelines with a drag-and-drop node editor. 24+ node types across triggers, actions, conditionals, and exports.</li>
            <li><strong>MCP Server</strong> — Connect AI assistants directly to your document stores via Model Context Protocol.</li>
            <li><strong>Node Documentation</strong> — In-app documentation for every workflow node with configuration guides, examples, and tips.</li>
            <li><strong>Smart Extraction (Gemini)</strong> — AI-powered structured data extraction using Google Gemini with JSON schema definitions.</li>
            <li><strong>Workflow Execution History</strong> — Complete execution logs with per-node timing, input/output data, and error details.</li>
          </ul>
        </div>

        <div class="wf-use-case">
          <p class="wf-use-case-title">v2.4.0 — Enhanced Document Processing</p>
          <p class="wf-use-case-flow">December 2025</p>
          <ul class="wf-list">
            <li><strong>Cloud Vision OCR</strong> — Google Cloud Vision integration for high-accuracy OCR on complex documents.</li>
            <li><strong>Document Comparison</strong> — Side-by-side comparison of extracted data between two documents with similarity scoring.</li>
            <li><strong>Bulk Upload</strong> — Upload multiple documents at once with automatic processing queue management.</li>
            <li><strong>Processing Reports</strong> — Detailed analytics on processing volume, accuracy, throughput, and error patterns.</li>
          </ul>
        </div>

        <div class="wf-use-case">
          <p class="wf-use-case-title">v2.3.0 — User Management & SSO</p>
          <p class="wf-use-case-flow">October 2025</p>
          <ul class="wf-list">
            <li><strong>Role-Based Access Control</strong> — Built-in roles (Super Admin, Admin, Editor, Viewer) plus custom roles with granular permissions.</li>
            <li><strong>SSO Integration</strong> — SAML 2.0 and OIDC support for Azure AD, Google Workspace, Okta, and custom IdPs.</li>
            <li><strong>User Groups</strong> — Organize users into groups with shared document store and workflow access.</li>
            <li><strong>User Invitations</strong> — Invite users via email with pre-assigned roles and groups.</li>
            <li><strong>Audit Trail</strong> — Comprehensive logging of all user and system actions.</li>
          </ul>
        </div>

        <div class="wf-use-case">
          <p class="wf-use-case-title">v2.2.0 — Integrations & Triggers</p>
          <p class="wf-use-case-flow">August 2025</p>
          <ul class="wf-list">
            <li><strong>Email Triggers</strong> — Gmail and Outlook inbox monitoring with filter-based document ingestion.</li>
            <li><strong>OneDrive Integration</strong> — Bi-directional OneDrive support: trigger on new files, upload processed results.</li>
            <li><strong>Webhook System</strong> — Configurable outgoing webhooks for real-time event notifications.</li>
            <li><strong>HTTP Trigger</strong> — Accept documents and data via REST API endpoints.</li>
          </ul>
        </div>

        <div class="wf-use-case">
          <p class="wf-use-case-title">v2.1.0 — Template System & Search</p>
          <p class="wf-use-case-flow">June 2025</p>
          <ul class="wf-list">
            <li><strong>Extraction Templates</strong> — Define reusable field schemas for different document types.</li>
            <li><strong>Full-Text Search</strong> — Search across all document content with filters, facets, and saved queries.</li>
            <li><strong>Field Mapping Rules</strong> — Configure how extracted values are parsed, formatted, and validated.</li>
            <li><strong>Export Nodes</strong> — Generate Excel, Word, and PDF documents from processed data.</li>
          </ul>
        </div>

        <div class="wf-use-case">
          <p class="wf-use-case-title">v2.0.0 — DocIQ Platform Launch</p>
          <p class="wf-use-case-flow">April 2025</p>
          <ul class="wf-list">
            <li><strong>Document Upload & Storage</strong> — Secure document upload with multi-format support and organized document stores.</li>
            <li><strong>OCR Processing</strong> — Built-in OCR for text extraction from scanned documents and images.</li>
            <li><strong>Dashboard</strong> — Central overview with document counts, processing status, and quick actions.</li>
            <li><strong>Basic Search</strong> — Search by document name, tags, and metadata.</li>
            <li><strong>MongoDB Backend</strong> — Scalable document storage with MongoDB.</li>
          </ul>
        </div>
      </div>
    `,
  },
];
